import React, { Fragment, useContext, useEffect, useRef } from "react";
import ChatAppContext from "../../../Chat";
import avatar from "../../../assets/images/avatar.png";
import { format, isToday, isYesterday } from "date-fns";
import { formatTimeAMPM, convertHtmlToJsx } from "../../Common/CommonFunction";
import Spinner from "../../Common/Spinner";
import ChatMessageAttachment from "../../Common/ChatMessageAttachment";
import {
  isSenderAdmin,
  isSenderJobseeker,
  isSenderRecruiter,
} from "../../../utils/chatSenders";

const ChatMessage = () => {
  const chatContainerRef = useRef();
  const {
    messages = {},
    chatLoading,
    selectedUser,
    currentUser,
    selectedUserProfile,
    ticketMeta = [],
  } = useContext(ChatAppContext) || {};

  const userData = selectedUser;

  const renderDateSeparator = (date) => {
    if (isToday(new Date(date))) return "Today";
    if (isYesterday(new Date(date))) return "Yesterday";
    return format(new Date(date), "yyyy-MM-dd");
  };

  const avatarFor = (item) => {
    if (isSenderAdmin(item.sender_type)) {
      return currentUser?.profile_image || avatar;
    }
    if (isSenderRecruiter(item.sender_type)) {
      return (
        selectedUser?.profile_image ||
        selectedUserProfile?.recruiter_profile_image ||
        avatar
      );
    }
    return userData?.jobseeker_photo || avatar;
  };

  /** Admin (you) on the right; jobseeker / recruiter on the left. */
  const bubbleClass = (item) => {
    if (isSenderAdmin(item.sender_type)) {
      return "message other-message float-end border border-primary";
    }
    return "message my-message float-start";
  };

  const imgFloat = (item) => {
    if (isSenderAdmin(item.sender_type)) return "float-end";
    return "float-start";
  };

  const timeAlignClass = (item) =>
    isSenderAdmin(item.sender_type) ? "text-end" : "text-start";

  const senderLabel = (item) => {
    const senderName = String(item?.sender_name || "").trim();
    const senderRole = String(item?.sender_role_title || "").trim();

    if (senderName && senderRole) return `${senderName} (${senderRole})`;
    if (senderName) return senderName;
    if (senderRole) return senderRole;
    if (isSenderAdmin(item.sender_type)) return "Admin";
    if (isSenderRecruiter(item.sender_type)) return "Recruiter";
    if (isSenderJobseeker(item.sender_type)) return "Jobseeker";
    return "User";
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Fragment>
      {chatLoading ? (
        <div className="spinner-container">
          <Spinner />
        </div>
      ) : (
        <div
          className="chat-history chat-msg-box custom-scrollbar"
          ref={chatContainerRef}
        >
          <ul className="simple-list">
            {userData?.chat_type !== "support" ? (
              <li className="clearfix border-0 mb-4">
                <div
                  style={{ width: "90%", margin: "auto" }}
                  className="message other-message float-start"
                >
                  <h6>Moderation view</h6>
                  <p className="mb-0 small">
                    You are viewing a conversation between a jobseeker and a
                    recruiter. Messages you send appear as{" "}
                    <strong>Admin</strong> and are visible to both parties.
                  </p>
                </div>
              </li>
            ) : null}
            {userData?.chat_type === "support" && ticketMeta && ticketMeta.length > 0 ? (
              <li className="clearfix border-0 mb-4">
                <div className="ticket-meta-box">
                  <h6 className="ticket-meta-title">Ticket Details</h6>
                  <dl className="ticket-meta-list">
                    {ticketMeta.map((item) => (
                      <div key={item.meta_id} className="ticket-meta-item">
                        <dt className="ticket-meta-label">{item.question}</dt>
                        <dd className="ticket-meta-value">
                          {item.file_url ? (
                            <a
                              href={item.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ticket-meta-link"
                            >
                              📎 View Attachment
                            </a>
                          ) : (
                            item.answer
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </li>
            ) : null}
            {Object.keys(messages).length > 0 ? (
              Object.keys(messages).map((date) => (
                <Fragment key={date}>
                  <li className="date-separator mb-4 text-center">
                    <span className="badge bg-primary">
                      {renderDateSeparator(date)}
                    </span>
                  </li>
                  {messages[date].map((item) => (
                    <li className="clearfix border-0" key={item.message_id}>
                      <div
                        style={{ maxWidth: "100%", width: "min(100%, 420px)" }}
                        className={bubbleClass(item)}
                      >
                        <img
                          className={`rounded-circle ${imgFloat(
                            item
                          )} chat-user-img img-30`}
                          src={avatarFor(item)}
                          alt=""
                        />
                        <div className={`message-data ${timeAlignClass(item)}`}>
                          <span className="message-data-time small text-muted">
                            {senderLabel(item)} · {" "}
                            {formatTimeAMPM(item.created_at)}
                          </span>
                        </div>
                        {convertHtmlToJsx(item.message_text)}
                        <ChatMessageAttachment item={item} />
                      </div>
                    </li>
                  ))}
                </Fragment>
              ))
            ) : (
              <div className="p-4 text-center text-muted small">
                No messages in this thread yet.
              </div>
            )}
          </ul>
        </div>
      )}
    </Fragment>
  );
};

export default ChatMessage;
